import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { MessageCircle, Clock, Bot, Phone, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const MessengerSettings = () => {
  const { hasPermission } = useAuth();
  const [settings, setSettings] = useState({
    business_hours_enabled: true,
    business_hours_start: '08:00',
    business_hours_end: '17:00',
    business_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    after_hours_message: '',
    ai_summary_enabled: true,
    ai_summary_time: '17:00',
    default_outbound_number: null,
  });
  const [outboundNumbers, setOutboundNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canEdit = hasPermission('manage_system_settings');

  useEffect(() => {
    fetchSettings();
    fetchOutboundNumbers();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/messenger/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch messenger settings:', err);
      toast.error('Failed to load messenger settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutboundNumbers = async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/admin/outbound-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { outbound_numbers: [...] } not an array directly
      setOutboundNumbers(res.data.outbound_numbers || res.data || []);
    } catch (err) {
      console.error('Failed to fetch outbound numbers:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.put(`${API_BASE}/api/messenger/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Messenger settings saved successfully');
    } catch (err) {
      console.error('Failed to save messenger settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (day) => {
    setSettings(prev => ({
      ...prev,
      business_days: prev.business_days.includes(day)
        ? prev.business_days.filter(d => d !== day)
        : [...prev.business_days, day]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Business Hours Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            <CardTitle>Business Hours</CardTitle>
          </div>
          <CardDescription>
            Configure when staff are available to respond to SMS messages. 
            Auto-replies will be sent outside these hours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="business_hours_enabled">Enable Business Hours</Label>
              <p className="text-sm text-muted-foreground">
                Turn on to automatically send after-hours messages
              </p>
            </div>
            <Switch
              id="business_hours_enabled"
              checked={settings.business_hours_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, business_hours_enabled: checked }))}
              disabled={!canEdit}
              data-testid="business-hours-toggle"
            />
          </div>

          {settings.business_hours_enabled && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_hours_start">Start Time</Label>
                  <Input
                    id="business_hours_start"
                    type="time"
                    value={settings.business_hours_start}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_hours_start: e.target.value }))}
                    disabled={!canEdit}
                    data-testid="business-hours-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_hours_end">End Time</Label>
                  <Input
                    id="business_hours_end"
                    type="time"
                    value={settings.business_hours_end}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_hours_end: e.target.value }))}
                    disabled={!canEdit}
                    data-testid="business-hours-end"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Days</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`day-${day.value}`}
                        checked={settings.business_days.includes(day.value)}
                        onCheckedChange={() => handleDayToggle(day.value)}
                        disabled={!canEdit}
                        data-testid={`business-day-${day.value}`}
                      />
                      <Label htmlFor={`day-${day.value}`} className="text-sm cursor-pointer">
                        {day.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="after_hours_message">After Hours Message</Label>
                <Textarea
                  id="after_hours_message"
                  value={settings.after_hours_message}
                  onChange={(e) => setSettings(prev => ({ ...prev, after_hours_message: e.target.value }))}
                  placeholder="Thank you for reaching out. You have reached us outside our normal chat hours. We'll respond on the next business day."
                  rows={3}
                  disabled={!canEdit}
                  data-testid="after-hours-message"
                />
                <p className="text-xs text-muted-foreground">
                  This message is automatically sent when a participant texts outside business hours.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* AI Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            <CardTitle>AI Daily Summary</CardTitle>
          </div>
          <CardDescription>
            Configure automatic AI-generated summaries of daily SMS conversations. 
            Summaries are saved as case notes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="ai_summary_enabled">Enable AI Summaries</Label>
              <p className="text-sm text-muted-foreground">
                Generate daily conversation summaries automatically
              </p>
            </div>
            <Switch
              id="ai_summary_enabled"
              checked={settings.ai_summary_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_summary_enabled: checked }))}
              disabled={!canEdit}
              data-testid="ai-summary-toggle"
            />
          </div>

          {settings.ai_summary_enabled && (
            <div className="space-y-2 max-w-xs">
              <Label htmlFor="ai_summary_time">Summary Generation Time</Label>
              <Input
                id="ai_summary_time"
                type="time"
                value={settings.ai_summary_time}
                onChange={(e) => setSettings(prev => ({ ...prev, ai_summary_time: e.target.value }))}
                disabled={!canEdit}
                data-testid="ai-summary-time"
              />
              <p className="text-xs text-muted-foreground">
                Time when AI summaries are generated (default: 5:00 PM)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Default Outbound Number Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            <CardTitle>Default Outbound Number</CardTitle>
          </div>
          <CardDescription>
            Select the default phone number for sending auto-reply messages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-w-md">
            <Label htmlFor="default_outbound_number">Phone Number</Label>
            <Select
              value={settings.default_outbound_number || ''}
              onValueChange={(value) => setSettings(prev => ({ ...prev, default_outbound_number: value || null }))}
              disabled={!canEdit}
            >
              <SelectTrigger data-testid="default-outbound-select">
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {outboundNumbers.map((num) => (
                  <SelectItem key={num.id} value={num.phone_number}>
                    {num.label || num.phone_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              This number will be used for sending after-hours auto-replies.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      {canEdit && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            data-testid="save-messenger-settings"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MessengerSettings;
